import React from 'react';
import { ResponsiveTabs } from '../../../Components/Common/DashboardKit';
import HrEmbeddedPage from './HrEmbeddedPage';

/**
 * Punches — find, review, add, edit, or correct a punch.
 * (Round: HR UI Simplification — Round 2, user feedback.)
 *
 * Feedback addressed:
 *  1. "Punch editor same as legacy" — the PUNCH EDITOR is now the
 *     DEFAULT view of this section, exactly like the legacy HR menu
 *     where "Punch Editor" (ClockEditPunch) was the primary tool:
 *     type an employee → results → select → act. The component is the
 *     existing PunchEditorPage, unchanged.
 *  2. "Too many sub-cards / redundant" — "Missing punches" and
 *     "Hourly missing punches" were two pills over the SAME layout
 *     component (MissingPunchLayout) differing only by endpoint.
 *     They are now ONE pill with a small Salaried | Hourly switch
 *     inside — same two endpoints, same wrappers, nothing removed.
 *
 * Pills: Punch editor (default) · Missing punches · Pending requests.
 * Old URLs keep working: ?mode=salaried / ?mode=hourly are normalized
 * by the shell to ?mode=missing&class=… before this renders.
 */
const LazyEditor   = React.lazy(() => import('../PunchEditor/PunchEditorPage'));
const LazySalaried = React.lazy(() => import('../MissingPunches'));
const LazyHourly   = React.lazy(() => import('../MissingPunchesHourly'));
const LazyPending  = React.lazy(() => import('../PendingPunchRequests'));

const MODES = [
    { key: 'editor',  label: 'Punch editor',     icon: 'ri-edit-2-line' },
    { key: 'missing', label: 'Missing punches',  icon: 'ri-error-warning-line' },
    { key: 'pending', label: 'Pending requests', icon: 'ri-inbox-line' },
];

const HrPunchesSection = ({ mode, punchClass, onModeChange, onClassChange }) => {
    const active = MODES.some((m) => m.key === mode) ? mode : 'editor';
    const cls = punchClass === 'hourly' ? 'hourly' : 'salaried';

    return (
        <div>
            <div className="hr-section-title">
                <div>
                    <h5>Punches</h5>
                    <span className="text-muted">Search → results → select → act.</span>
                </div>
            </div>

            <ResponsiveTabs
                tabs={MODES}
                activeKey={active}
                onChange={onModeChange}
                variant="pills"
                size="sm"
            />

            {active === 'editor' && <HrEmbeddedPage><LazyEditor /></HrEmbeddedPage>}

            {active === 'missing' && (
                <div>
                    {/* Salaried | Hourly — one place, two existing endpoints. */}
                    <div
                        className="btn-group btn-group-sm mt-3"
                        role="group"
                        aria-label="Missing punches employee class"
                    >
                        <button
                            type="button"
                            className={`btn ${cls === 'salaried' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => onClassChange('salaried')}
                        >
                            Salaried
                        </button>
                        <button
                            type="button"
                            className={`btn ${cls === 'hourly' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => onClassChange('hourly')}
                        >
                            Hourly
                        </button>
                    </div>
                    {/* key remounts the layout so dates/list reset per class,
                        matching what switching pills did before. */}
                    <HrEmbeddedPage key={cls}>
                        {cls === 'salaried' ? <LazySalaried /> : <LazyHourly />}
                    </HrEmbeddedPage>
                </div>
            )}

            {active === 'pending' && <HrEmbeddedPage><LazyPending /></HrEmbeddedPage>}
        </div>
    );
};

export default HrPunchesSection;
